// tslint:disable-next-line:interface-name
interface ColorComponents {
  red: number;
  green: number;
  blue: number;
}

function checkByteValue(byteValue: number): number {
  if (byteValue > 255) {
    return 255;
  }
  if (byteValue < 0) {
    byteValue = 0;
  }
  return byteValue;
}

function usesPoundNotation(htmlColorCode: string): boolean {
  if (typeof htmlColorCode === 'string') {
    if (htmlColorCode[0] === '#') {
      return true;
    }
  }
  return false;
}

function removePoundPrefix(htmlColorCode: string): string {
  if (usesPoundNotation(htmlColorCode)) {
    return htmlColorCode.slice(1);
  }
  return htmlColorCode;
}

function toInteger(htmlColorCode: string | number): number {
  let colorValue: number;
  if (typeof htmlColorCode === 'string') {
    htmlColorCode = removePoundPrefix(htmlColorCode);
    if (htmlColorCode && htmlColorCode.length === 3) {
      htmlColorCode += htmlColorCode;
    }
    colorValue = parseInt(htmlColorCode, 16);
  } else {
    colorValue = htmlColorCode;
  }
  return colorValue;
}

function getColorComponents(htmlColorCode: string | number): ColorComponents {
  const colorValue = toInteger(htmlColorCode);
  return {
    // tslint:disable-next-line:no-bitwise
    red: colorValue >> 16,
    // tslint:disable-next-line:no-bitwise
    blue: (colorValue >> 8) & 0x00FF,
    // tslint:disable-next-line:no-bitwise
    green: colorValue & 0x0000FF,
  };
}

function toHtmlColorCode(colorComponents: ColorComponents, usePound: boolean = true): string {
  // tslint:disable-next-line:no-bitwise
  let colorValue = (colorComponents.green | (colorComponents.blue << 8) | (colorComponents.red << 16)).toString(16);
  if (colorValue.length < 6) {
    colorValue = '0'.repeat(6 - colorValue.length) + colorValue;
  }
  return (usePound ? '#' : '') + colorValue;
}

function getAmountUnit(amount: string | number): string {
  if (typeof amount === 'string') {
    amount = (amount || '').trim();
    return amount[amount.length - 1];
  }
  return undefined;
}

function parseAmount(amount: string | number): { value: number, unit?: string } {
  return {
    value: parseInt('' + amount, 10),
    unit: getAmountUnit(amount)
  };
}

function add(value: number, amount: string | number): number {
  const parsedAmount = parseAmount(amount);
  if (parsedAmount.unit === '%') {
    return parseInt('' + (value * (1 + (parsedAmount.value / 100))), 10);
  }
  return value + parsedAmount.value;
}

function adjustColor(htmlColorCode: string | number, amount: string | number): string {
  const usePound = (typeof htmlColorCode === 'string') && usesPoundNotation(htmlColorCode);
  const color = getColorComponents(htmlColorCode);
  const adjustedColor: ColorComponents = {
    red: checkByteValue(add(color.red, amount)),
    blue: checkByteValue(add(color.blue, amount)),
    green: checkByteValue(add(color.green, amount))
  };
  return toHtmlColorCode(adjustedColor, usePound);
}

function lightenColor(htmlColorCode: string | number, amount: string | number): string {
  const parsedAmount = parseAmount(amount);
  return adjustColor(htmlColorCode, `${Math.abs(parsedAmount.value)}${parsedAmount.unit}`);
}

function darkenColor(htmlColorCode: string | number, amount: string | number): string {
  const parsedAmount = parseAmount(amount);
  return adjustColor(htmlColorCode, `${-1 * Math.abs(parsedAmount.value)}${parsedAmount.unit}`);
}

function invertColor(htmlColorCode: string | number): string {
  const usePound = (typeof htmlColorCode === 'string') && usesPoundNotation(htmlColorCode);
  const color = getColorComponents(htmlColorCode);
  const invertedColor: ColorComponents = {
    red: checkByteValue(255 - color.red),
    blue: checkByteValue(255 - color.blue),
    green: checkByteValue(255 - color.green)
  };
  return toHtmlColorCode(invertedColor, usePound);
}

function colorSimilarity(htmlColorCodeA: string | number, htmlColorCodeB: string | number): number {
  const colorA = getColorComponents(htmlColorCodeA);
  const colorB = getColorComponents(htmlColorCodeB);
  const difference = {
    red: Math.abs(colorB.red - colorA.red) / 255,
    blue: Math.abs(colorB.blue - colorA.blue) / 255,
    green: Math.abs(colorB.green - colorA.green) / 255
  };
  return 1 - ((difference.red + difference.blue + difference.green) / 3);
}

function colorDifference(htmlColorCodeA: string | number, htmlColorCodeB: string | number): number {
  return toInteger(htmlColorCodeB) - toInteger(htmlColorCodeA);
}

export { ColorComponents, getColorComponents, adjustColor, lightenColor, darkenColor, invertColor, colorSimilarity, colorDifference };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/helper/colour/index.ts